import cn from "classnames";
import { useState } from "react";
import axios, { AxiosError } from "axios";
import { useAppDispatch } from "app/hooks";
import { useIntl, FormattedMessage } from "react-intl";

// MUI components
import {
    Box,
    Button,
    Grid,
    Typography,
    TextField,
    IconButton,
    CircularProgress,
    Skeleton,
    InputAdornment,
} from "@mui/material";

// Components
import CustomIcon from "components/CustomIcon";

// Store
import { fetchSession } from "store/slice/auth/authSlice";
import { setToastConfig } from "store/slice/UI";

// Services
import { updateUserDetailsService, updatePasswordService, updatePasswordErrorResponse } from "services";

// Icons
import icons from "enums/icons";
import CancelIcon from "@mui/icons-material/Cancel";

// Const
import { initialPassword } from "../AccountManager";

// Styles
import accountSettingsStyles from "./AccountSettings.styles";

// Types
import { isEmailValid } from "utils";
import { AccountSettingsProps } from "./AccountSettings.types";
import { urlPaths } from "enums/urlPaths";
import { useNavigate } from "react-router-dom";

const defaultShowPassword = { current: false, new: false, confirm: false };
const defaultPasswordValidation = [
    { id: 1, message: "12 characters or more", regex: `^.{12,}$`, isValid: false },
    { id: 2, message: "1 or more special characters", regex: `[^A-Za-z0-9]`, isValid: false },
    { id: 3, message: "1 or more upper case letters", regex: `[A-Z]`, isValid: false },
    { id: 4, message: "1 or more numerals", regex: `[0-9]`, isValid: false },
];

const LoadingBars = () => (
    <>
        <Skeleton animation="wave" sx={{ fontSize: 42 }} />
        <Skeleton variant="rectangular" animation="wave" height={60} />
    </>
);

const AccountSettings = ({
    name,
    setName,
    email,
    setEmail,
    password,
    setPassword,
    userData,
    sessionData,
    isLoading,
}: AccountSettingsProps) => {
    const intl = useIntl();
    const classes = accountSettingsStyles();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(defaultShowPassword);
    const [showPasswordValidationMessages, setShowPasswordValidationMessages] = useState(false);
    const [passwordValidationMessages, setPasswordValidationMessages] = useState(defaultPasswordValidation);

    const nameInitialValue = userData?.auth_data?.firstName || "";
    const lastNameInitialValue = userData?.auth_data?.lastName || "";
    const emailInitialValue = sessionData?.authn.user.username || "";

    const shouldDisabledPasswordButton =
        password.status === "saving" ||
        !password.current.length ||
        !password.new.length ||
        !password.confirm_new.length ||
        password.new !== password.confirm_new ||
        passwordValidationMessages.some((p) => !p.isValid);

    const handleSaveName = async () => {
        try {
            setName((currentVal) => ({ ...currentVal, status: "saving" }));
            await updateUserDetailsService({
                first_name: name.firstName,
                last_name: name.lastName,
                email: email.value,
            });
            dispatch(
                setToastConfig({
                    title: "Name Changed",
                    content: "Your name has been changed successfully.",
                })
            );
            dispatch(fetchSession());
        } catch (error) {
            console.error(error);
        } finally {
            setName((currentVal) => ({ ...currentVal, status: "pristine" }));
        }
    };

    const handleSaveEmail = async () => {
        try {
            setEmail((currentVal) => ({ ...currentVal, status: "saving", invalid: false }));
            await updateUserDetailsService({
                first_name: name.firstName,
                last_name: name.lastName,
                email: email.value,
            });
            dispatch(
                setToastConfig({
                    title: "Email Changed",
                    content: `Your email has been successfully changed to ${email.value}`,
                })
            );
        } catch (error) {
            console.error(error);
        } finally {
            setEmail((currentVal) => ({ ...currentVal, status: "pristine" }));
        }
    };

    const handleSavePassword = async () => {
        setPassword((currentVal) => ({ ...currentVal, error: undefined }));

        try {
            setPassword((currentVal) => ({ ...currentVal, status: "saving" }));
            await updatePasswordService({
                current_password: password.current,
                new_password: password.new,
            });
            setPassword(initialPassword);
            setShowPassword(defaultShowPassword);
            setShowPasswordValidationMessages(false);
            setPasswordValidationMessages(defaultPasswordValidation);
            dispatch(
                setToastConfig({
                    title: "Password Changed",
                    content: "Your password has been changed successfully.",
                })
            );
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const err = error as AxiosError<updatePasswordErrorResponse>;
                if (error.response?.status === 401) {
                    console.error(err.response?.data.detail.message);
                    setPassword((currentVal) => ({
                        ...currentVal,
                        status: "error",
                        errorCurrentPass: err.response?.data.detail.message,
                    }));
                }

                if (error.response?.status === 400) {
                    setPassword((currentVal) => ({
                        ...currentVal,
                        status: "error",
                        errorNewPass: err.response?.data.detail.message,
                    }));
                }
            }
        }
    };

    const handleShowPassword = (passwordType: "current" | "new" | "confirm") => {
        setShowPassword((currentVal) => ({ ...currentVal, [passwordType]: !currentVal[passwordType] }));
    };

    const handleSetPassword = (ev: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const newPassword = ev.target.value;
        setPassword((currentVal) => ({
            ...currentVal,
            new: newPassword,
            errorNewPass: newPassword ? password.errorNewPass : undefined,
        }));
        setPasswordValidationMessages((prevVal) =>
            prevVal.map((e) => {
                const regex = new RegExp(e.regex, "g");
                return { ...e, isValid: regex.test(newPassword) };
            })
        );
    };

    const handleBlurNewPassword = (shouldShow: boolean) => {
        if (password.new.length && showPasswordValidationMessages) return;
        setShowPasswordValidationMessages(shouldShow);
    };

    return (
        <Box className={classes.main}>
            {/* Personal Details Section */}
            <Box className={classes.row}>
                {isLoading ? (
                    <LoadingBars />
                ) : (
                    <>
                        <Typography variant="h6" className={classes.rowTitle}>
                            <FormattedMessage id="accountManager.accountSettings.personalDetails" />
                        </Typography>
                        <Box className={classes.rowDetails}>
                            <Typography className={classes.text}>
                                <FormattedMessage id="accountManager.accountSettings.nameIs" />{" "}
                                {name.status === "pristine" ? (
                                    <strong>{`${name.firstName} ${name.lastName}`}</strong>
                                ) : null}
                            </Typography>
                            {name.status === "editing" || name.status === "saving" ? (
                                <>
                                    <TextField
                                        value={name.firstName}
                                        className={classes.textField}
                                        disabled={name.status === "saving"}
                                        placeholder={intl.formatMessage({
                                            id: "accountManager.accountSettings.placeholderName",
                                        })}
                                        onChange={(ev) =>
                                            setName((currentVal) => ({ ...currentVal, firstName: ev.target.value }))
                                        }
                                    />
                                    <TextField
                                        value={name.lastName}
                                        className={classes.textField}
                                        disabled={name.status === "saving"}
                                        placeholder={intl.formatMessage({
                                            id: "accountManager.accountSettings.placeholderlastName",
                                        })}
                                        onChange={(ev) =>
                                            setName((currentVal) => ({ ...currentVal, lastName: ev.target.value }))
                                        }
                                    />
                                    <IconButton
                                        disabled={name.status === "saving"}
                                        onClick={() =>
                                            setName({
                                                firstName: nameInitialValue,
                                                lastName: lastNameInitialValue,
                                                status: "pristine",
                                            })
                                        }
                                    >
                                        <CancelIcon sx={{ color: "error.main", ml: 2, mr: 4 }} fontSize="medium" />
                                    </IconButton>
                                    <Button
                                        variant="contained"
                                        onClick={handleSaveName}
                                        disabled={
                                            name.status === "saving" ||
                                            !name.firstName.trim().length ||
                                            !name.lastName.trim().length
                                        }
                                        endIcon={
                                            name.status === "saving" ? (
                                                <CircularProgress size={16} color="inherit" />
                                            ) : (
                                                <CustomIcon icon={icons.chevronRightThin} />
                                            )
                                        }
                                    >
                                        <FormattedMessage id="accountManager.accountSettings.saveChanges" />
                                    </Button>
                                </>
                            ) : null}

                            {name.status === "editing" || name.status === "saving" ? null : (
                                <Button
                                    variant="text"
                                    className={classes.textBtn}
                                    onClick={() => setName((currentVal) => ({ ...currentVal, status: "editing" }))}
                                >
                                    <FormattedMessage id="accountManager.accountSettings.change" />
                                </Button>
                            )}
                        </Box>
                    </>
                )}
            </Box>

            {/* Email Section */}
            <Box className={classes.row}>
                {isLoading ? (
                    <LoadingBars />
                ) : (
                    <>
                        <Typography variant="h6" className={classes.rowTitle}>
                            <FormattedMessage id="accountManager.accountSettings.email" />
                        </Typography>
                        <Box className={classes.rowDetails}>
                            <Typography className={classes.text}>
                                <FormattedMessage id="accountManager.accountSettings.emailIs" />{" "}
                                {email.status === "pristine" ? <strong>{email.value}</strong> : null}
                            </Typography>
                            {email.status === "editing" || email.status === "saving" ? (
                                <>
                                    <TextField
                                        value={email.value}
                                        error={email.invalid}
                                        className={cn(classes.textField, classes.absoluteHelper)}
                                        disabled={email.status === "saving"}
                                        onChange={(ev) => setEmail({ ...email, value: ev.target.value })}
                                        onBlur={() => {
                                            setEmail((currentVal) => ({
                                                ...currentVal,
                                                invalid: !isEmailValid(currentVal.value),
                                            }));
                                        }}
                                        helperText={email.invalid ? "Invalid email address" : ""}
                                        placeholder={intl.formatMessage({
                                            id: "accountManager.accountSettings.placeholderEmail",
                                        })}
                                    />
                                    <IconButton
                                        disabled={email.status === "saving"}
                                        onClick={() =>
                                            setEmail((currentVal) => ({
                                                ...currentVal,
                                                value: emailInitialValue,
                                                status: "pristine",
                                            }))
                                        }
                                    >
                                        <CancelIcon sx={{ color: "error.main", ml: 2, mr: 4 }} fontSize="medium" />
                                    </IconButton>
                                    <Button
                                        variant="contained"
                                        endIcon={
                                            email.status === "saving" ? (
                                                <CircularProgress size={16} color="inherit" />
                                            ) : (
                                                <CustomIcon icon={icons.chevronRightThin} />
                                            )
                                        }
                                        onClick={handleSaveEmail}
                                        disabled={email.status === "saving" || email.invalid}
                                    >
                                        <FormattedMessage id="accountManager.accountSettings.saveChanges" />
                                    </Button>
                                </>
                            ) : null}

                            {email.status === "editing" || email.status === "saving" ? null : (
                                <Button
                                    variant="text"
                                    className={classes.textBtn}
                                    onClick={() => setEmail({ ...email, status: "editing" })}
                                >
                                    <FormattedMessage id="accountManager.accountSettings.change" />
                                </Button>
                            )}
                        </Box>
                        <FormattedMessage
                            id="accountManager.accountSettings.emailAdviceNote"
                            values={{
                                link: (chunks: React.ReactNode) => (
                                    <Typography
                                        component="span"
                                        color="secondary"
                                        className={classes.textBtn}
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => navigate(urlPaths.AccountPrivacy)}
                                    >
                                        {chunks}
                                    </Typography>
                                ),
                            }}
                        />
                    </>
                )}
            </Box>

            {/* Password Section */}
            <Box className={classes.row}>
                {isLoading ? (
                    <LoadingBars />
                ) : (
                    <>
                        <Typography variant="h6" className={classes.rowTitle}>
                            <FormattedMessage id="accountManager.accountSettings.password" />
                        </Typography>
                        <Grid container spacing={2} className={classes.passwordGrid}>
                            <Grid item lg={3}>
                                <Typography className={classes.passwordTitle}>
                                    <FormattedMessage id="accountManager.accountSettings.currentPassword" />
                                </Typography>
                                <TextField
                                    autoComplete="off"
                                    sx={{ width: "100%" }}
                                    type={showPassword.current ? "text" : "password"}
                                    error={password.status === "error" && !!password.errorCurrentPass}
                                    value={password.current}
                                    className={cn(classes.passwordField, classes.absoluteHelper)}
                                    disabled={password.status === "saving"}
                                    helperText={password.errorCurrentPass}
                                    placeholder={intl.formatMessage({
                                        id: "accountManager.accountSettings.placeholderCurrentPassword",
                                    })}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleShowPassword("current")}>
                                                    <CustomIcon
                                                        sx={{ fontSize: 16 }}
                                                        icon={showPassword.current ? icons.eyeStriked : icons.eye}
                                                    />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    onChange={(ev) => {
                                        const newValue = ev.target.value;
                                        setPassword({
                                            ...password,
                                            current: newValue,
                                            errorCurrentPass: newValue ? password.errorCurrentPass : undefined,
                                            // current: newValue,
                                            // errorCurrentPass: undefined,
                                        });
                                    }}
                                />
                            </Grid>
                            <Grid item lg={3}>
                                <Typography className={classes.passwordTitle}>
                                    <FormattedMessage id="accountManager.accountSettings.newPassword" />
                                </Typography>
                                <TextField
                                    autoComplete="off"
                                    type={showPassword.new ? "text" : "password"}
                                    value={password.new}
                                    sx={{ width: "100%" }}
                                    className={classes.absoluteHelper}
                                    error={password.status === "error" && !!password.errorNewPass}
                                    disabled={password.status === "saving"}
                                    helperText={password.errorNewPass}
                                    placeholder={intl.formatMessage({
                                        id: "accountManager.accountSettings.placeholderNewPassword",
                                    })}
                                    onFocus={() => handleBlurNewPassword(true)}
                                    onBlur={() => handleBlurNewPassword(false)}
                                    onChange={handleSetPassword}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleShowPassword("new")}>
                                                    <CustomIcon
                                                        sx={{ fontSize: 16 }}
                                                        icon={showPassword.new ? icons.eyeStriked : icons.eye}
                                                    />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {showPasswordValidationMessages && (
                                    <Box className={classes.passwordValidationBox}>
                                        <Typography>
                                            <FormattedMessage id="accountManager.accountSettings.passwordMustContain" />
                                        </Typography>
                                        <ul className={classes.passwordValidationList}>
                                            {passwordValidationMessages.map((validation) => (
                                                <li key={validation.id}>
                                                    <CustomIcon
                                                        sx={{ fontSize: 20 }}
                                                        icon={validation.isValid ? icons.greenTick : icons.redCross}
                                                    />
                                                    <Box sx={{ fontWeight: validation.isValid ? 400 : 700 }}>
                                                        {validation.message}
                                                    </Box>
                                                </li>
                                            ))}
                                        </ul>
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={3} lg={3}>
                                <Typography className={classes.passwordTitle}>
                                    <FormattedMessage id="accountManager.accountSettings.confirmNewPassword" />
                                </Typography>
                                <TextField
                                    autoComplete="off"
                                    sx={{ width: "100%" }}
                                    value={password.confirm_new}
                                    disabled={password.status === "saving"}
                                    type={showPassword.confirm ? "text" : "password"}
                                    className={cn(classes.passwordField, classes.absoluteHelper)}
                                    placeholder={intl.formatMessage({
                                        id: "accountManager.accountSettings.placeholderConfirmNewPassword",
                                    })}
                                    onChange={(ev) => setPassword({ ...password, confirm_new: ev.target.value })}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleShowPassword("confirm")}>
                                                    <CustomIcon
                                                        sx={{ fontSize: 16 }}
                                                        icon={showPassword.confirm ? icons.eyeStriked : icons.eye}
                                                    />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item lg={3}>
                                <Button
                                    variant="contained"
                                    onClick={handleSavePassword}
                                    disabled={shouldDisabledPasswordButton}
                                    sx={{ mt: 4 }}
                                    endIcon={
                                        password.status === "saving" ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <CustomIcon icon={icons.chevronRightThin} />
                                        )
                                    }
                                >
                                    <FormattedMessage id="accountManager.accountSettings.savePassword" />
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default AccountSettings;
